import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  Check,
  ExternalLink,
  LayoutDashboard,
  Mail,
  MapPin,
  Search,
  Star,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { getReferralCode, setReferralCookie } from "@/lib/referral";
import { Seo } from "@/components/seo/Seo";
import { getSiteUrl } from "@/lib/siteUrl";
import { getVendorTestimonials, type Testimonial } from "@/lib/testimonials";
import TrustBadges from "@/components/trust/TrustBadges";
import { SUBSCRIPTIONS_ENABLED } from "@/config/features";
import {
  useForBusinessesContent,
  type RequestServiceId,
  type RequestTemplateId,
  SELF_SERVE_TIER_IDS,
} from "@/hooks/useForBusinessesContent";

const revealUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      className={className}
      variants={revealUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, ease: "easeOut", delay }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({
  badge,
  title,
  description,
}: {
  badge?: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="max-w-3xl">
      {badge ? <Badge variant="outline" className="mb-3">{badge}</Badge> : null}
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{title}</h2>
      {description ? (
        <p className="mt-3 text-lg text-gray-600 leading-relaxed">{description}</p>
      ) : null}
    </div>
  );
}

export default function ForBusinesses() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, tx } = useI18n();
  const content = useForBusinessesContent(t);
  const {
    tiers,
    requestServices,
    requestServiceTemplates,
    portfolioProjects,
    priorityCities,
    painPoints,
    valuePillars,
    benefitBlocks,
    onboardingSteps,
    freeIncludes,
    vitrineLines,
    heroStats,
    showcaseRows,
    formChips,
    faqItems,
    faqJsonLd,
  } = content;
  const formAnchorId = "sales-contact";
  const formRef = useRef<HTMLFormElement | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<RequestTemplateId | null>(null);

  const applyRequestTemplate = (
    id: RequestTemplateId,
    options: { scroll?: boolean } = {},
  ) => {
    setSelectedRequest(id);
    const tpl = requestServiceTemplates[id];
    const formEl = formRef.current;
    if (formEl) {
      const ta = (messageRef.current ||
        (formEl.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null)) as
        | HTMLTextAreaElement
        | null;
      if (ta) ta.value = tpl;
    }
    if (options.scroll) {
      document.getElementById(formAnchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const code = getReferralCode();
    if (code) setReferralCookie(code);
  }, []);

  useEffect(() => {
    (async () => {
      const vendorTestimonials = await getVendorTestimonials(3);
      setTestimonials(vendorTestimonials);
    })();
  }, []);

  const refCode = getReferralCode();
  const scrollToElement = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToCompanySignup = async (upgrade?: "scale" | "pro") => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      const qs = new URLSearchParams();
      qs.set("type", "company");
      if (refCode) qs.set("ref", refCode);
      navigate(`/auth?${qs.toString()}`);
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (!profile?.id) {
      navigate("/company/onboarding");
      return;
    }

    const { data: companies } = await (supabase as any)
      .from("companies")
      .select("id, onboarding_completed, is_deleted, is_active, created_at")
      .eq("profile_id", profile.id)
      .eq("is_deleted", false)
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    const activeCompanies = Array.isArray(companies) ? companies : [];
    const completedCompany = activeCompanies.find((company: any) => company?.onboarding_completed);

    if (completedCompany) {
      if (upgrade === "scale" || upgrade === "pro") {
        navigate(`/company/dashboard?b2b_upgrade=${upgrade}`);
        return;
      }
      navigate("/company/dashboard");
      return;
    }

    navigate("/company/onboarding");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload = {
      company_name: String(formData.get("company_name") || ""),
      email: String(formData.get("email") || ""),
      phone: String(formData.get("phone") || ""),
      region: String(formData.get("region") || ""),
      message: String(formData.get("message") || ""),
      service_interest: selectedRequest || "",
      ref_code: refCode || "",
      landing_page: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      source: "for-businesses",
    };
    try {
      setSubmitting(true);
      const r = await fetch("/api/notifications?action=contact_sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.error || t("business.toast.error_send"));
      }
      setSent(true);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast({
        title: t("business.toast.failed_title"),
        description: error?.message || t("business.toast.failed_desc"),
        variant: "destructive",
      });
      return;
    } finally {
      (e.currentTarget as HTMLFormElement).reset();
      setSubmitting(false);
    }
    toast({
      title: t("business.toast.thanks_title"),
      description: t("business.toast.thanks_desc"),
    });
  };

  const siteUrl = getSiteUrl();
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Halomora",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: t("business.jsonld.org.desc"),
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Vendite B2B",
      email: "alessandro@halomora.com",
      availableLanguage: ["Italian", "English"],
    },
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: t("business.jsonld.service.type"),
    provider: { "@type": "Organization", name: "Halomora" },
    areaServed: { "@type": "Country", name: "Italy" },
    offers: SUBSCRIPTIONS_ENABLED
      ? tiers.map((plan) => ({
          "@type": "Offer",
          name: plan.name,
          price: plan.price.replace(/[^0-9]/g, "") || "0",
          priceCurrency: "EUR",
          description: plan.headline,
        }))
      : [
          {
            "@type": "Offer",
            name: t("business.jsonld.offer.free.name"),
            price: "0",
            priceCurrency: "EUR",
            description: t("business.jsonld.offer.free.desc"),
          },
        ],
  };

  const faqJsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqJsonLd.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <div className="min-h-screen app-page">
      <Seo
        title={t("business.seo.title")}
        description={t("business.seo.description")}
        canonicalPath="/for-businesses"
        jsonLd={[organizationJsonLd, serviceJsonLd, faqJsonLdSchema]}
      />

      {/* ——— HERO ——— */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.28),transparent_34%),radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_30%),linear-gradient(180deg,rgba(15,23,42,0.95),rgba(2,6,23,0.98))]" />
        <div className="relative container mx-auto px-6 py-10 md:py-16">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-end mb-6">
              <Button
                variant="outline"
                className="bg-white/5 border-white/20 text-white hover:bg-white/12"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("business.nav.back_home")}
              </Button>
            </div>

            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <Reveal>
                <div className="max-w-3xl">
                  <Badge className="mb-4 border border-white/15 bg-white text-primary hover:bg-white">
                    {t("business.hero.badge")}
                  </Badge>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
                    {t("business.hero.title")}
                  </h1>
                  <p className="text-slate-200 mt-5 text-lg md:text-xl leading-relaxed max-w-2xl">
                    {t("business.hero.subtitle")}
                  </p>
                  <div className="mt-7 flex flex-col sm:flex-row gap-3">
                    <Button size="lg" className="shadow-xl" onClick={() => goToCompanySignup()}>
                      {t("business.hero.cta_signup")}
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-white/5 border-white/20 text-white hover:bg-white/12"
                      onClick={() => scrollToElement("come-funziona")}
                    >
                      {t("business.hero.cta_how")}
                    </Button>
                  </div>
                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    {heroStats.map((item) => (
                      <div
                        key={item.label}
                        className="rounded-2xl border border-white/12 bg-white/8 px-4 py-4 text-slate-100 backdrop-blur-sm"
                      >
                        <div className="text-sm font-medium text-white">{item.label}</div>
                        <div className="text-sm text-slate-300 mt-1">{item.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                <div className="rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(5,10,26,0.96)_0%,rgba(15,23,42,0.96)_100%)] p-6 text-white shadow-[0_38px_110px_-42px_rgba(2,8,23,0.92)]">
                  <div className="text-sm font-medium text-slate-300 mb-1">{t("business.showcase.label")}</div>
                  <div className="text-xl font-semibold text-white mb-5">{t("business.showcase.title")}</div>
                  <div className="space-y-3 rounded-2xl border border-white/10 bg-white/6 p-4">
                    {showcaseRows.map((row) => (
                      <div key={row.label} className="flex items-center gap-3 text-sm">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${row.done ? "bg-emerald-400" : "bg-white/30"}`}
                        />
                        <span className={row.done ? "text-slate-100" : "text-slate-400"}>{row.label}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-slate-400 leading-relaxed">{t("business.showcase.hint")}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12 md:py-16 space-y-16 md:space-y-20">
        {/* ——— PROBLEMA ——— */}
        <Reveal>
          <section className="space-y-6">
            <SectionHeading
              badge={t("business.problem.badge")}
              title={t("business.problem.title")}
              description={t("business.problem.desc")}
            />
            <div className="grid gap-4 md:grid-cols-2">
              {painPoints.map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-gray-200 bg-gray-50/80 px-5 py-4 text-gray-700 text-sm leading-relaxed"
                >
                  {item}
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ——— SOLUZIONE ——— */}
        <Reveal>
          <section className="space-y-6">
            <SectionHeading
              badge={t("business.solution.badge")}
              title={t("business.solution.title")}
              description={t("business.solution.desc")}
            />
            <div className="grid gap-5 md:grid-cols-3">
              {valuePillars.map((pillar) => (
                <Card key={pillar.title} className="border-gray-200 shadow-card h-full">
                  <CardContent className="p-6">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-4">
                      <pillar.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{pillar.title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{pillar.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ——— VETRINA ——— */}
        <Reveal>
          <section className="grid gap-6 lg:grid-cols-2 lg:items-center">
            <div className="space-y-4">
              <Badge variant="outline">{t("business.vitrine.badge")}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                {t("business.vitrine.title")}
              </h2>
              <p className="text-gray-600 leading-relaxed">{t("business.vitrine.desc")}</p>
              <ul className="space-y-3">
                {vitrineLines.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-gray-700 text-sm">
                    <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" strokeWidth={2.5} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-500/5 shadow-card">
              <CardContent className="p-6 space-y-4">
                <div className="rounded-xl border border-primary/15 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {t("business.vitrine.sample_url")}
                  </div>
                  <div className="mt-2 text-lg font-semibold text-gray-900">{t("business.vitrine.sample_name")}</div>
                  <p className="mt-1 text-sm text-gray-600">{t("business.vitrine.sample_desc")}</p>
                </div>
                <p className="text-sm text-gray-600">{t("business.vitrine.custom_site")}</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button onClick={() => navigate("/for-businesses/booking-system-demo")}>
                    Vedi demo booking system
                  </Button>
                  <Button variant="outline" onClick={() => scrollToElement("servizi-opzionali")}>
                    {t("business.vitrine.cta")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </Reveal>

        {/* ——— SITO + BOOKING SYSTEM ——— */}
        <Reveal>
          <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="space-y-4">
              <Badge variant="outline">Sito professionale + Halomora</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                Il tuo sito professionale, già collegato a Halomora.
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Vai online in pochi giorni con un sito moderno, ottimizzato per la tua zona e già collegato a richieste,
                preventivi e prenotazioni nel backend Halomora.
              </p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {[
                  "Design professionale mobile-first",
                  "SEO locale per città e servizi",
                  "Richieste preventivo e booking online",
                  "Clienti e richieste gestiti in un unico posto",
                  "Powered by Halomora, ma con il tuo brand",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm text-gray-700">
                    <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" strokeWidth={2.5} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => navigate("/for-businesses/booking-system-demo")}>
                  Guarda le demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open("https://jservicejesolo.com/", "_blank", "noopener,noreferrer")}
                >
                  Esempio reale: JService
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Card className="overflow-hidden border-primary/20 bg-white shadow-card">
              <CardContent className="p-0">
                <div className="grid gap-0 md:grid-cols-[0.9fr_1.1fr]">
                  <div className="bg-slate-950 p-6 text-white">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-300">
                      Front-end per la tua impresa
                    </div>
                    <h3 className="mt-3 text-2xl font-bold leading-tight">
                      Non solo una vetrina: un sito che porta richieste dentro Halomora.
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-300">
                      Il cliente vede il tuo brand. Tu ricevi contatti, servizi richiesti, zone e dati ordinati in un
                      flusso operativo.
                    </p>
                  </div>
                  <div className="space-y-3 p-5">
                    {[
                      ["Sito", "Homepage, servizi, zone, FAQ e form preventivo."],
                      ["Booking", "Richieste e prenotazioni online 24/7."],
                      ["Backend", "Gestione clienti centralizzata in Halomora."],
                    ].map(([label, text]) => (
                      <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">{label}</div>
                        <div className="mt-1 text-sm text-slate-700">{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </Reveal>

        {/* ——— VANTAGGI ——— */}
        <Reveal>
          <section className="space-y-6">
            <SectionHeading
              badge={t("business.benefits.badge")}
              title={t("business.benefits.title")}
              description={t("business.benefits.desc")}
            />
            <div className="grid gap-5 md:grid-cols-2">
              {benefitBlocks.map((block) => (
                <Card key={block.title} className="border-gray-200 shadow-card h-full">
                  <CardContent className="p-6 flex gap-4">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <block.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{block.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{block.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ——— GRATIS + COMMISSIONE ——— */}
        <Reveal>
          <Card className="app-panel border-slate-200/60 shadow-card" id="piano-gratuito">
            <CardHeader className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                  {t("business.free.badge_forever")}
                </Badge>
                <Badge variant="outline">{t("business.free.badge_no_sub")}</Badge>
              </div>
              <CardTitle className="text-2xl md:text-3xl text-slate-950">{t("business.free.title")}</CardTitle>
              <p className="text-slate-600 leading-relaxed">{t("business.free.desc")}</p>
            </CardHeader>
            <CardContent className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
              <ul className="space-y-3">
                {freeIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="h-4 w-4 shrink-0 mt-1 text-primary" strokeWidth={2.5} />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-3">
                <Button
                  variant="hero"
                  size="lg"
                  className="w-full py-6 text-base font-semibold"
                  onClick={() => goToCompanySignup()}
                >
                  {t("business.free.cta")}
                </Button>
                <p className="text-xs text-slate-500 text-center">{t("business.free.hint")}</p>
              </div>
            </CardContent>
          </Card>
        </Reveal>

        {/* ——— ONBOARDING ——— */}
        <Reveal>
          <section className="space-y-6" id="come-funziona">
            <SectionHeading
              badge={t("business.onboarding.badge")}
              title={t("business.onboarding.title")}
              description={t("business.onboarding.desc")}
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {onboardingSteps.map((item) => (
                <div key={item.step} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white font-semibold text-sm">
                    {item.step}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ——— SERVIZI OPZIONALI ——— */}
        {!SUBSCRIPTIONS_ENABLED && (
          <Reveal>
            <section className="space-y-6" id="servizi-opzionali">
              <SectionHeading
                badge={t("business.optional.badge")}
                title={t("business.optional.title")}
                description={t("business.optional.desc")}
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {requestServices.map((svc) => (
                  <Card key={svc.id} className="flex flex-col border app-panel border-slate-200/60 shadow-card">
                    <CardHeader>
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-2">
                        <svc.icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-xl text-slate-950">{svc.name}</CardTitle>
                      <p className="text-slate-600 text-sm">{svc.headline}</p>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <ul className="space-y-2 flex-1">
                        {svc.bullets.slice(0, 4).map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                            <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full mt-6"
                        variant="outline"
                        onClick={() => applyRequestTemplate(svc.id, { scroll: true })}
                      >
                        {svc.cta}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* ——— PORTFOLIO (JService) ——— */}
        {!SUBSCRIPTIONS_ENABLED && portfolioProjects.length > 0 && (
          <Reveal>
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent-green-500/5 shadow-card">
              <CardHeader>
                <Badge variant="outline" className="w-fit mb-1">
                  {t("business.portfolio.badge")}
                </Badge>
                <CardTitle className="text-2xl">{t("business.portfolio.title")}</CardTitle>
                <p className="text-sm text-gray-600">{t("business.portfolio.desc")}</p>
              </CardHeader>
              <CardContent>
                {portfolioProjects.map((project) => (
                  <motion.a
                    key={project.id}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -3 }}
                    className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-primary/40 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">
                          {project.city}
                        </div>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">{project.client}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{project.sector}</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-slate-400 group-hover:text-primary shrink-0" />
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">{project.description}</p>
                    <ul className="mt-4 space-y-2">
                      {project.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={2.5} />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-primary">
                      <span>{t("business.portfolio.visit")}</span>
                      <ExternalLink className="h-4 w-4" />
                    </div>
                  </motion.a>
                ))}
                <p className="mt-5 text-xs text-gray-500">{t("business.portfolio.disclaimer")}</p>
              </CardContent>
            </Card>
          </Reveal>
        )}

        {/* ——— TESTIMONIANZE (solo se reali dal DB) ——— */}
        {testimonials.length > 0 && (
          <Reveal>
            <section className="space-y-6">
              <SectionHeading title={t("business.testimonials.title")} />
              <div className="grid md:grid-cols-3 gap-6">
                {testimonials.map((item) => (
                  <Card key={item.id} className="border-gray-200 shadow-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                        <span className="font-semibold">{item.rating}/5</span>
                      </div>
                      <p className="text-gray-700 mb-4 italic text-sm leading-relaxed">"{item.quote}"</p>
                      <div className="font-semibold text-gray-900">
                        {item.company || t("business.testimonials.company_fallback")}
                      </div>
                      {item.city ? <div className="text-sm text-gray-500">{item.city}</div> : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </Reveal>
        )}

        {/* ——— CTA FINALE ——— */}
        <Reveal>
          <div className="relative overflow-hidden rounded-[1.75rem] border border-primary/25 shadow-[0_28px_70px_-34px_rgba(37,99,235,0.4)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.35),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.22),transparent_32%),linear-gradient(135deg,hsl(223,72%,38%)_0%,hsl(217,78%,32%)_48%,hsl(266,65%,36%)_100%)]" />
            <div className="relative px-6 py-10 md:py-12 text-center space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">{t("business.cta.title")}</h2>
              <p className="text-slate-100/90 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
                {t("business.cta.desc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                <Button size="lg" variant="hero" onClick={() => goToCompanySignup()}>
                  {t("business.cta.signup")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/35 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                  onClick={() => scrollToElement(formAnchorId)}
                >
                  {t("business.cta.talk")}
                </Button>
              </div>
            </div>
          </div>
        </Reveal>

        {/* ——— SEO local links ——— */}
        <Reveal>
          <Card className="border-gray-200 shadow-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                {t("business.local.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">{t("business.local.desc")}</p>
              <div className="flex flex-wrap gap-2">
                {priorityCities.map((c) => (
                  <Button
                    key={c.slug}
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/aziende/pulizie/${c.slug}`)}
                  >
                    {tx("business.local.btn", { city: c.name })}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </Reveal>

        <Reveal>
          <Card className="border-gray-200 shadow-card">
            <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm text-gray-600 max-w-2xl">{t("business.transparency")}</p>
              <TrustBadges variant="compact" />
            </CardContent>
          </Card>
        </Reveal>

        {/* ——— FORM ——— */}
        <Reveal>
          <Card className="border-gray-200 shadow-card" id={formAnchorId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {t("business.sales.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sent && (
                <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-4" role="status">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <div className="font-medium">{t("business.form.sent_title")}</div>
                      <div>{t("business.form.sent_desc")}</div>
                    </div>
                  </div>
                </div>
              )}
              <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="company_name"
                  placeholder={t("business.form.company")}
                  required
                  disabled={submitting}
                />
                <Input
                  name="email"
                  type="email"
                  placeholder={t("business.form.email")}
                  required
                  disabled={submitting}
                />
                <Input name="phone" placeholder={t("business.form.phone")} disabled={submitting} />
                <Input name="region" placeholder={t("business.form.region")} disabled={submitting} />

                {!SUBSCRIPTIONS_ENABLED && (
                  <div className="md:col-span-2 space-y-2">
                    <div className="text-sm font-medium text-gray-900">{t("business.form.interest")}</div>
                    <div className="flex flex-wrap gap-2">
                      {formChips.map((chip) => (
                        <button
                          key={chip.id}
                          type="button"
                          onClick={() => applyRequestTemplate(chip.id)}
                          aria-pressed={selectedRequest === chip.id}
                          disabled={submitting}
                          className={
                            selectedRequest === chip.id
                              ? "rounded-full border border-primary bg-primary px-4 py-1.5 text-sm font-medium text-white"
                              : "rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:border-primary/40"
                          }
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <Textarea
                    ref={messageRef}
                    name="message"
                    rows={6}
                    placeholder={t("business.form.message")}
                    disabled={submitting}
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <Button type="submit" disabled={submitting}>
                    {submitting
                      ? t("business.form.submitting")
                      : sent
                        ? t("business.form.sent_btn")
                        : t("business.form.submit")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </Reveal>

        {/* ——— FAQ ——— */}
        <Reveal>
          <Card className="border-gray-200 shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">{t("business.faq.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-lg border border-gray-200 p-4 bg-white">
                  <summary className="cursor-pointer font-medium text-gray-900">{item.q}</summary>
                  <p className="mt-2 text-gray-600 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </CardContent>
          </Card>
        </Reveal>

        {/* Subscription tiers — hidden in phase 1 */}
        {SUBSCRIPTIONS_ENABLED && (
          <div className="space-y-5" id="business-plans">
            <SectionHeading
              badge={t("business.plans.badge")}
              title={t("business.plans.title")}
              description={t("business.plans.desc")}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tiers.map((plan) => (
                <Card
                  key={plan.id}
                  className={`flex flex-col min-h-[380px] border ${
                    plan.highlighted ? "ring-2 ring-primary/20" : ""
                  }`}
                >
                  <CardHeader>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <p className="text-sm text-slate-600">{plan.headline}</p>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <div className="text-2xl font-bold">
                      {plan.price}
                      <span className="text-base font-normal text-slate-600">{plan.period}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      {tx("business.plans.commission", { rate: plan.commission })}
                    </p>
                    <Button
                      className="w-full mt-auto"
                      onClick={() => {
                        if (plan.id === "free") goToCompanySignup();
                        else if (SELF_SERVE_TIER_IDS.has(plan.id)) {
                          goToCompanySignup(plan.id === "scale" ? "scale" : "pro");
                        } else scrollToElement(formAnchorId);
                      }}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
